/**
 * Linking a Hugging Face account to the paired machine, from any page.
 *
 * The site never holds the token: it sends a bare action to the machine
 * (agent/hf_actions.py), which runs Hugging Face's device flow and hands back
 * a code and a link. The person approves in any browser -- this one or a
 * phone -- and the machine keeps the token. The page only ever learns the
 * account name and which gated models it may download.
 */
import { APIClient } from "../api/client.js";
import { closeModal, openModal } from "../components/Modal.js";
import { toast } from "../components/Toast.js";
import { h, setChildren } from "../components/dom.js";
import { store } from "../state/store.js";
import type { GenerationJob, GenerationJobKind, JobEvent } from "../types/index.js";
import { routeToOwnMachine } from "./hosted-jobs.js";

const POLL_MS = 2000;
// The Hub's device codes last fifteen minutes; a little longer than that.
const GIVE_UP_MS = 16 * 60 * 1000;

function targetWorker(): string | null {
	const target = store.get().executionTarget;
	return target.kind === "hosted" ? target.worker.worker_id : null;
}

type Outcome = { ok: true; data: Record<string, unknown> } | { ok: false; error: string };

/** Send one Hugging Face action and follow it; `onEvent` sees every event. */
async function runAction(
	kind: GenerationJobKind,
	workerId: string | null,
	onEvent: (event: JobEvent) => void = () => {}
): Promise<Outcome> {
	let job: GenerationJob;
	try {
		job = await APIClient.submitHostedJob({}, routeToOwnMachine(workerId ?? targetWorker()), kind);
	} catch (error) {
		return { ok: false, error: error instanceof Error ? error.message : String(error) };
	}
	const started = Date.now();
	let cursor = 0;
	while (Date.now() - started < GIVE_UP_MS) {
		try {
			const page = await APIClient.getJobEventPage(job.job_id, cursor);
			cursor = page.next_cursor;
			for (const event of page.events) onEvent(event);
		} catch {
			// The job's status below stays authoritative.
		}
		const current = await APIClient.getJob(job.job_id);
		const result = (current.result ?? {}) as { data?: Record<string, unknown>; error?: string };
		if (current.status === "succeeded") return { ok: true, data: result.data ?? {} };
		if (current.status === "failed" || current.status === "cancelled") {
			return { ok: false, error: result.error ?? "Hugging Face n'a pas pu être relié." };
		}
		await new Promise<void>((resolve) => window.setTimeout(resolve, POLL_MS));
	}
	return { ok: false, error: "Le code a expiré avant d'être validé. Recommencez la connexion." };
}

/**
 * Link the machine's Hugging Face account: show the code and the link while
 * the person approves it. Resolves with the account name, or null.
 */
export async function linkHuggingFace(workerId: string | null = null): Promise<string | null> {
	const body = h("div", { class: "hf-link" });
	const waiting = (text: string) => setChildren(body, h("p", { class: "help-text" }, text));
	waiting("Votre ordinateur demande un code à Hugging Face…");
	openModal({
		title: "Relier Hugging Face",
		content: body,
		actions: [{ label: "Fermer", onClick: () => closeModal() }],
	});
	const outcome = await runAction("hf_connect", workerId, (event) => {
		const meta = event.metadata ?? {};
		if (meta.stage !== "hf_login") return;
		const link = String(meta.verification_uri ?? "https://huggingface.co/oauth/device");
		setChildren(
			body,
			h("p", null, "1. Ouvrez cette page, ici ou sur votre téléphone :"),
			h(
				"p",
				null,
				h(
					"a",
					{ href: link, target: "_blank", rel: "noopener noreferrer", class: "btn" },
					"Ouvrir Hugging Face ↗"
				)
			),
			h("p", null, "2. Vérifiez que ce code s'affiche :"),
			h("p", { class: "hf-link-code" }, String(meta.user_code ?? "")),
			h(
				"p",
				null,
				"3. Cliquez « Autoriser ». Pas encore de compte ? Créez-en un gratuitement sur la même page."
			),
			h("p", { class: "help-text" }, "Cette fenêtre se met à jour toute seule.")
		);
	});
	if (!outcome.ok) {
		waiting(outcome.error);
		return null;
	}
	closeModal();
	const account = String(outcome.data.hf_account ?? "");
	toast(`Hugging Face relié : ${account}.`, "success");
	return account || null;
}

/**
 * Ask the machine to check again which gated licences its account accepted,
 * after the person says they did. Resolves with the Hub's answers, or null.
 */
export async function checkHuggingFaceAccess(
	workerId: string | null = null
): Promise<Record<string, boolean> | null> {
	const outcome = await runAction("hf_check_access", workerId);
	if (!outcome.ok) {
		toast(outcome.error, "error");
		return null;
	}
	return (outcome.data.gated_access ?? {}) as Record<string, boolean>;
}

/** Unlink: the machine forgets its token and what it could access. */
export async function unlinkHuggingFace(workerId: string | null = null): Promise<boolean> {
	const outcome = await runAction("hf_disconnect", workerId);
	if (!outcome.ok) toast(outcome.error, "error");
	return outcome.ok;
}
